import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Respiratory subsection adapter
 * Handles respiratory assessment, cough, oxygen, and lung sounds (5 fields)
 */
export class RespiratoryAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Respiratory assessment fields
    const respiratoryStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL
    )?.at(0)

    const cough = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH
    )?.at(0)

    const oxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN
    )?.at(0)

    const lungSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS
    )

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.RESPIRATORY_STATUS]: respiratoryStatus,
      [SocFormField.RESPIRATORY_COUGH]: cough,
      [SocFormField.RESPIRATORY_OXYGEN]: oxygen,
      [SocFormField.RESPIRATORY_LUNG_SOUNDS]: lungSounds,
      [SocFormField.RESPIRATORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL,
        value: this.getFormDataField(SocFormField.RESPIRATORY_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN,
        value: this.getFormDataField(SocFormField.RESPIRATORY_OXYGEN)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS,
        value: this.getFormDataField(SocFormField.RESPIRATORY_LUNG_SOUNDS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.RESPIRATORY_SYSTEM_SUMMARY)
      }
    ]

    if (this.formDataFieldExists(SocFormField.RESPIRATORY_COUGH)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH,
        value: this.getFormDataField(SocFormField.RESPIRATORY_COUGH)
      })
    }

    return mutations
  }
}
